import React from 'react';
import s from '../../s';
import * as util from '../../../util';
import ModalSimple from '../../../global/modal-simple';
import ReactCrop, {centerCrop, Crop, makeAspectCrop, PixelCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {action, computed, makeObservable, observable} from 'mobx';
import {observer} from 'mobx-react';
import InfoIcon from '../shared/info-icon';
import {imageUploadStore} from './image-upload.store';

const centerAspectCrop = (width: number, height: number, aspect: number) => {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 100,
            },
            aspect,
            width,
            height,
        ),
        width,
        height,
    );
};

@observer
export default class ImageUpload extends React.Component {
    @observable clicked: boolean;
    @observable imgSrc: string;
    @observable aspect: number;
    @observable crop: Crop | undefined;
    @observable completedCrop: PixelCrop;
    @observable imgRef: React.RefObject<HTMLImageElement> = React.createRef<HTMLImageElement>();

    constructor(props) {
        super(props);
        this.clicked = false;
        makeObservable(this);
    }

    @computed get canUpload(): boolean {
        return this.clicked;
    }

    @action
    checkResoLevel = () => {
        if (s.ppi_w_actual < s.ppi_w || s.ppi_h_actual < s.ppi_h) {
            imageUploadStore.reso_level = 2;
        } else if (s.ppi_w_actual < s.ppi_w_max || s.ppi_h_actual < s.ppi_h_max) {
            imageUploadStore.reso_level = 1;
        }
    };

    @action
    save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        s.ppi_w_actual = Math.round(this.completedCrop.width);
        s.ppi_h_actual = Math.round(this.completedCrop.height);

        s.getPPIValuesForImageUpload();
        this.checkResoLevel();

        if (imageUploadStore.reso_level !== 2) {
            this.showImage();
        } else {
            imageUploadStore.cancel();
        }
    };

    @action
    showImage = () => {
        const url = URL.createObjectURL(imageUploadStore.file);

        if (url) {
            s.pcs.override_d1_img = url;
            s.ui.image_uploaded = true;
            s.ui.doRender = true;
            s.pulse();
            this.forceUpdate();
        } else {
            imageUploadStore.imageUploadError();
        }
    };

    @action
    createCroppedImage = (crop: PixelCrop) => {
        if (this.imgRef.current && crop.width && crop.height) {
            const {naturalWidth, naturalHeight, width, height} = this.imgRef.current;
            const scaleX = naturalWidth / width;
            const scaleY = naturalHeight / height;

            const canvas = document.createElement('canvas');
            canvas.width = crop.width;
            canvas.height = crop.height;

            const ctx = canvas.getContext('2d');

            if (!ctx) return;

            ctx.drawImage(this.imgRef.current, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);

            canvas.toBlob(blob => {
                if (!blob) return;
                imageUploadStore.file = new File([blob], imageUploadStore.file.name, {type: imageUploadStore.file.type});
            });

            this.completedCrop = {
                x: crop.x * scaleX,
                y: crop.y * scaleY,
                width: crop.width * scaleX,
                height: crop.height * scaleY,
                unit: 'px',
            };
        }
    };

    @action
    clear = async event => {
        event.preventDefault();
        s.ui.image_uploaded = false;
        s.ui.image_uploaded_msg = '';
        s.pcs.image_upload_hash3 = '';
        s.pcs.override_d1_img = '';
        s.ui.image_uploading = false;
        imageUploadStore.reso_level = 0;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        URL.revokeObjectURL(s.pcs.override_d1_img);
        s.pulse();
        this.forceUpdate();
    };

    @action
    selectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length) {
            imageUploadStore.file = e.target.files[0];
            this.imgSrc = '';
            this.clicked = true;
            util.modal('modal-image-crop');
            this.clicked = true;
            this.forceUpdate();
            this.crop = undefined;
            const reader = new FileReader();
            reader.addEventListener('load', () => (this.imgSrc = reader.result?.toString() || ''));
            reader.readAsDataURL(imageUploadStore.file);
        }
    };

    @action
    centerCrop = (e: React.SyntheticEvent<HTMLImageElement>) => {
        this.aspect = s.pcs.docWidth / s.pcs.docHeight;
        if (this.aspect) {
            const {width, height} = e.currentTarget;
            this.crop = centerAspectCrop(width, height, this.aspect);
        }
    };

    render() {
        if (!s.pcs.is_caf || !s.ui.show_cust || !s.ui.show_grad_year) {
            return (
                <div className="gray-box">
                    <div style={{padding: '6px 0px 12px 0px'}}>
                        <h1>{s.prod.DocType} Upload</h1>
                        <div style={{paddingTop: '6px'}}>
                            {!s.ui.image_uploaded && (
                                <input
                                    id="imageUploadInput"
                                    type="file"
                                    accept="image/*"
                                    onChange={this.selectFile}
                                />
                            )}
                            {s.ui.image_uploaded && (
                                <button
                                    type="submit"
                                    className="prod-atc-btn"
                                    style={{fontSize: '1rem', padding: '6px 12px'}}
                                    onClick={this.clear}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                        <div>
                            <ModalSimple
                                maxWidth={'800px'}
                                id="modal-image-crop"
                                title={'Image Crop'}
                                close={imageUploadStore.cancel}
                            >
                                <div>
                                    <p>Please crop the image first before proceeding.</p>
                                    <div style={{textAlign: 'center'}}>
                                        {!!this.imgSrc && (
                                            <ReactCrop
                                                onChange={(crop, percentCrop) => (this.crop = percentCrop)}
                                                onComplete={crop => this.createCroppedImage(crop)}
                                                crop={this.crop}
                                                aspect={this.aspect}
                                                minHeight={100}
                                            >
                                                <img
                                                    onLoad={this.centerCrop}
                                                    ref={this.imgRef}
                                                    src={this.imgSrc}
                                                    alt="Crop Image"
                                                />
                                            </ReactCrop>
                                        )}
                                        <form
                                            id="form-fiu"
                                            onSubmit={this.save}
                                        >
                                            {!!this.completedCrop && this.canUpload && (
                                                <button
                                                    type="submit"
                                                    className="prod-atc-btn"
                                                    style={{
                                                        fontSize: '1rem',
                                                        padding: '6px 12px',
                                                        marginTop: '10px',
                                                    }}
                                                    disabled={s.ui.image_uploading}
                                                    data-dismiss="modal"
                                                >
                                                    Upload
                                                </button>
                                            )}
                                        </form>
                                    </div>
                                </div>
                            </ModalSimple>
                        </div>

                        {!s.ui.image_uploaded && s.ui.image_uploaded_msg !== '' && (
                            <div style={{paddingTop: '6px'}}>
                                <div className="alert alert-danger">
                                    <p>
                                        <b>Upload {s.prod.DocType} Image</b>
                                    </p>
                                    <p>{s.ui.image_uploaded_msg}</p>
                                </div>
                            </div>
                        )}

                        {imageUploadStore.reso_level === 2 && (
                            <div style={{paddingTop: '6px'}}>
                                <div className="alert alert-danger">
                                    <p>
                                        <b>Image Resolution Error</b>
                                    </p>
                                    <p>
                                        A minimum resolution of {s.ppi_w}x{s.ppi_h} pixels is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed. For an enhanced visual experience, we strongly recommend uploading images with a
                                        resolution of at least {s.ppi_w_max}x{s.ppi_h_max} pixels. Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print. The resolution of the image uploaded is {s.ppi_w_actual}x{s.ppi_h_actual}.
                                    </p>
                                </div>
                            </div>
                        )}

                        {imageUploadStore.reso_level === 1 && (
                            <div style={{paddingTop: '6px'}}>
                                <div className="alert alert-info">
                                    <p>
                                        <b>Image Resolution</b>
                                    </p>
                                    <p>
                                        Your photo has the basic level of clarity and detail when printed. However, for an enhanced visual experience, we strongly recommend uploading images with a resolution of at least {s.ppi_w_max}x{s.ppi_h_max} pixels. Higher resolution images allow for finer
                                        details to be captured and displayed, resulting in a more vibrant and lifelike print. The resolution of the image uploaded is {s.ppi_w_actual}x{s.ppi_h_actual}.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div>
                        <ModalSimple
                            id="modal-image-crop-info"
                            title={'Image Size, Cropping and Aspect Ratio'}
                        >
                            <div>
                                <p>
                                    <b>Image Resolution / Size</b>
                                </p>
                                <p>
                                    A minimum resolution of {s.ppi_w}x{s.ppi_h} pixels is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed. For an enhanced visual experience, we strongly recommend uploading images with a
                                    resolution of at least {s.ppi_w_max}x{s.ppi_h_max} pixels. Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                                </p>
                                {s.si.UploadFee > 0 && <p>A printing fee of {util.currency(s.si.UploadFee)} will be added to the item upon upload.</p>}
                            </div>
                        </ModalSimple>
                        <span style={{fontSize: '0.875rem'}}>
                            Image size, cropping and aspect ratio
                            <InfoIcon
                                id="modal-image-crop-info"
                                ariaLabel="View information about Image Size, Cropping and Aspect Ratio"
                                useMargin={true}
                            />
                        </span>
                    </div>
                </div>
            );
        } else {
            return <div className="hide" />;
        }
    }
}
